import React from 'react';
import { ListTree, Plus, Save, Search, Trash2, Wifi } from 'lucide-react';
import { PanelSection } from '../ui/PanelSection';
import { Chip } from '../ui/Chip';
import { EmptyNote } from '../ui/EmptyNote';
import { NumberStepper } from '../ui/NumberStepper';
import { SegmentedChoice } from '../ui/SegmentedChoice';
import { TypedConfirmDialog } from '../ui/TypedConfirmDialog';
import { Field } from '../ui/Field';
import { useRcuLink } from '../useRcuLink';
import { TONE } from '../../../lib/rcu/catalog/feedback';
import {
  LED_SELECTOR_KIND,
  LED_SELECTOR_SPEC,
  MAPPING_ACTION,
  MAPPING_ACTION_FIELDS,
  MAPPING_ACTION_KEYS,
  MAPPING_ACTION_PART_LABEL,
  MAPPING_ATYPE,
  MAPPING_TIMED_ACTIONS,
  MAPPING_TIMEOUT,
  MAPPING_TRIGGER,
} from '../../../lib/rcu/catalog/mapping';
import type { LedSelectorKind, MappingActionKey } from '../../../lib/rcu/catalog/mapping';
import { DALI_MODE, daliModeLabel } from '../../../lib/rcu/catalog/dali';
import { RCU_GROUP, RCU_TOP_TYPE, moduleIdOfRail } from '../../../lib/rcu/catalog/protocol';
import {
  addDaliLedMapping,
  addLedMappings,
  addScene,
  deleteScene,
  eraseMappingsAndRestart,
  readLedMappings,
  readMappings,
  restart,
  wifiConfig,
} from '../../../lib/rcu/link/commands';
import { encodeAction, encodeScene } from '../../../lib/rcu/link/encode/mapping';
import type { ActionDraft } from '../../../lib/rcu/link/encode/mapping';
import { useRcuConfigStore } from '../../../stores/rcuConfigStore';
import { useRcuLinkStore } from '../../../stores/rcuLinkStore';

const BYTE_MIN = MAPPING_TRIGGER.MIN;
const BYTE_MAX = MAPPING_TRIGGER.MAX;

/** A numbers row with the labels the catalog gives that field's parts. */
const PartRow: React.FC<{
  labels: readonly string[];
  values: readonly number[];
  onChange: (parts: readonly number[]) => void;
  /** Index whose bound is not a byte, if any. */
  wideIndex?: number;
  wideMax?: number;
}> = ({ labels, values, onChange, wideIndex, wideMax }) => (
  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
    {labels.map((label, index) => (
      <NumberStepper
        key={label}
        label={label}
        value={values[index] ?? 0}
        min={BYTE_MIN}
        max={index === wideIndex ? (wideMax ?? BYTE_MAX) : BYTE_MAX}
        onChange={(next) =>
          onChange(values.map((value, at) => (at === index ? next : value)))
        }
      />
    ))}
  </div>
);

/** One action row of the scene editor: pick a field, fill its numbers. */
const ActionRow: React.FC<{
  draft: ActionDraft;
  onChange: (draft: ActionDraft) => void;
  onRemove: () => void;
}> = ({ draft, onChange, onRemove }) => {
  const labels = MAPPING_ACTION_PART_LABEL[draft.key];
  const expected = MAPPING_ACTION_FIELDS[draft.key];
  const isTimed = MAPPING_TIMED_ACTIONS.some((timed) => timed === draft.key);
  const encoded = encodeAction(draft);

  const choose = (key: MappingActionKey): void => {
    onChange({ key, parts: Array.from({ length: MAPPING_ACTION_FIELDS[key] }, () => BYTE_MIN) });
  };

  return (
    <div className="rounded-2xl border border-[#E0E0E0] px-3 py-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <SegmentedChoice
            label="Action"
            value={draft.key}
            columns={1}
            options={MAPPING_ACTION_KEYS.map((key) => ({
              value: key,
              label: `${key} · ${MAPPING_ACTION_PART_LABEL[key].join(' / ')}`,
            }))}
            onChange={choose}
          />
        </div>
        <button
          type="button"
          onClick={onRemove}
          title="Remove this action"
          className="mt-6 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[#666666] transition-colors hover:bg-[#F5F5F5] hover:text-[#C62828]"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-3">
        <PartRow
          labels={labels}
          values={draft.parts}
          onChange={(parts) => onChange({ key: draft.key, parts })}
          wideIndex={isTimed ? expected - 1 : undefined}
          wideMax={MAPPING_TIMEOUT.MAX}
        />
      </div>

      <p className="mt-2 font-mono text-xs text-[#666666]">{encoded.ok ? encoded.value : '—'}</p>
    </div>
  );
};

const SceneEditor: React.FC = () => {
  const config = useRcuConfigStore((state) => state.config);
  const live = useRcuLinkStore((state) => state.live);
  const { send, inFlight } = useRcuLink();
  const [confirmingDelete, setConfirmingDelete] = React.useState(false);
  const serial = config.rcu.serialNumber.trim();
  const [trigger, setTrigger] = React.useState<readonly number[]>([RCU_GROUP.DEFAULT, 1, 1, 0]);
  const [sceneKey, setSceneKey] = React.useState<number | null>(null);
  const [actions, setActions] = React.useState<readonly ActionDraft[]>([
    { key: MAPPING_ACTION.RELAY, parts: [1, 1] },
  ]);

  const entry = encodeScene({ trigger, key: sceneKey, actions });
  const triggerLabels = MAPPING_ACTION_PART_LABEL[MAPPING_ACTION.LED];

  return (
    <PanelSection
      title="Scenes"
      description="Write the scene a keypad press runs, read one back, or delete it."
      action={
        <span className="shrink-0">
          <Chip
            label={`${live.mappings.length} read`}
            tone={live.mappings.length > 0 ? TONE.SUCCESS : TONE.NEUTRAL}
          />
        </span>
      }
    >
      <div className="space-y-4">
        <div>
          <p className="mb-2 text-sm font-medium text-[#333333]">
            Trigger · group, panel, button, state
          </p>
          <PartRow
            labels={triggerLabels}
            values={trigger}
            onChange={setTrigger}
          />
        </div>

        <div className="rounded-2xl bg-[#F5F5F5] px-3 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-[#333333]">Key</p>
              <p className="text-xs text-[#666666]">
                Optional. The firmware suppresses every action of an entry whose key is zero.
              </p>
            </div>
            <div className="flex shrink-0 gap-1.5">
              <button
                type="button"
                onClick={() => setSceneKey(null)}
                className={`rounded-full border px-3 py-1.5 text-sm ${
                  sceneKey === null
                    ? 'border-[#D4A054] bg-[#FDF6E9] text-[#333333]'
                    : 'border-[#E0E0E0] bg-white text-[#666666]'
                }`}
              >
                None
              </button>
              <button
                type="button"
                onClick={() => setSceneKey(sceneKey ?? BYTE_MIN)}
                className={`rounded-full border px-3 py-1.5 text-sm ${
                  sceneKey !== null
                    ? 'border-[#D4A054] bg-[#FDF6E9] text-[#333333]'
                    : 'border-[#E0E0E0] bg-white text-[#666666]'
                }`}
              >
                Set
              </button>
            </div>
          </div>
          {sceneKey !== null && (
            <div className="mt-3">
              <NumberStepper
                label="Key value"
                value={sceneKey}
                min={BYTE_MIN}
                max={BYTE_MAX}
                onChange={setSceneKey}
                hint="Zero means the entry is stored but does nothing."
              />
            </div>
          )}
        </div>

        <div className="space-y-3">
          {actions.map((draft, index) => (
            <ActionRow
              key={index}
              draft={draft}
              onChange={(next) =>
                setActions((current) => current.map((value, at) => (at === index ? next : value)))
              }
              onRemove={() => setActions((current) => current.filter((_unused, at) => at !== index))}
            />
          ))}
          {actions.length === 0 && <EmptyNote message="A scene needs at least one action." />}
          <button
            type="button"
            onClick={() =>
              setActions((current) => [
                ...current,
                { key: MAPPING_ACTION.DIN, parts: [BYTE_MIN, BYTE_MIN, BYTE_MIN, BYTE_MIN] },
              ])
            }
            className="flex items-center gap-2 rounded-full border border-dashed border-[#E0E0E0] px-4 py-2 text-sm text-[#666666] transition-colors hover:border-[#D4A054] hover:text-[#333333]"
          >
            <Plus className="h-4 w-4" />
            Add action
          </button>
        </div>

        <div className="rounded-2xl bg-[#1A1A1A] px-3 py-3">
          <p className="text-xs uppercase tracking-wide text-white/50">Payload preview</p>
          <pre className="mt-1 overflow-x-auto font-mono text-xs text-white/80">
            {entry.ok
              ? JSON.stringify({ type: RCU_TOP_TYPE.ADD_MAPPING, data: [entry.value] }, null, 2)
              : '—'}
          </pre>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={inFlight > 0 || !entry.ok}
            onClick={() => {
              if (entry.ok) void send(addScene(entry.value), 'Scene written');
            }}
            className="flex items-center gap-2 rounded-full bg-[#1A1A1A] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#333333] disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            Write scene
          </button>
          <button
            type="button"
            disabled={inFlight > 0}
            onClick={() => void send(readMappings([trigger]), 'Scene read')}
            className="flex items-center gap-2 rounded-full border border-[#E0E0E0] px-4 py-2 text-sm font-medium text-[#666666] transition-colors hover:border-[#D4A054] hover:text-[#333333] disabled:opacity-50"
          >
            <Search className="h-4 w-4" />
            Read scene
          </button>
          <button
            type="button"
            disabled={inFlight > 0}
            onClick={() => setConfirmingDelete(true)}
            className="flex items-center gap-2 rounded-full border border-[#E0E0E0] px-4 py-2 text-sm font-medium text-[#666666] transition-colors hover:border-[#F44336] hover:text-[#C62828] disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" />
            Delete scene
          </button>
        </div>

        <TypedConfirmDialog
          isOpen={confirmingDelete}
          title="Delete this scene"
          message="The keypad press stops running a scene, including one a guest is using right now. Writing the scene again is the only way back."
          expected={serial === '' ? 'the RCU serial number' : serial}
          confirmLabel="Delete"
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={() => {
            setConfirmingDelete(false);
            void send(deleteScene(trigger), 'Scene deleted');
          }}
        />

        {!entry.ok && (
          <p className="text-sm text-[#C62828]">
            The payload is not valid yet: fix the numbers above to enable writing.
          </p>
        )}

        {live.mappings.length > 0 && (
          <div>
            <p className="text-sm font-medium text-[#333333]">Read back</p>
            {live.mappingsAt !== null && (
              <p className="text-xs text-[#666666]">
                Last read at {new Date(live.mappingsAt).toLocaleTimeString()}.
              </p>
            )}
            <ul className="mt-2 space-y-1">
              {live.mappings.map((mapping, index) => (
                <li
                  key={index}
                  className="rounded-xl bg-[#F5F5F5] px-3 py-2 font-mono text-xs text-[#666666]"
                >
                  {mapping.atype} · x=[{mapping.x.join(',')}]
                  {mapping.key === null ? '' : ` · k=${mapping.key}`}
                  {mapping.actions.map((part) => ` · ${part.field}="${part.raw}"`).join('')}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </PanelSection>
  );
};

const LedMappings: React.FC = () => {
  const config = useRcuConfigStore((state) => state.config);
  const live = useRcuLinkStore((state) => state.live);
  const { send, inFlight } = useRcuLink();
  const [kind, setKind] = React.useState<LedSelectorKind>(LED_SELECTOR_KIND.RELAY);
  const [selector, setSelector] = React.useState<readonly number[]>([1, BYTE_MIN]);
  const [led, setLed] = React.useState<readonly number[]>([RCU_GROUP.DEFAULT, 1, 1, 1]);
  const [daliLed, setDaliLed] = React.useState<readonly number[]>([0, 0, DALI_MODE.DIM, 0]);

  const ledField = encodeAction({ key: MAPPING_ACTION.LED, parts: led });
  const firstRail = config.rails[0];
  const moduleId = firstRail === undefined ? BYTE_MIN : moduleIdOfRail(firstRail.type);
  const modeIndex = MAPPING_ACTION_PART_LABEL[MAPPING_ACTION.DALI].indexOf('Mode');

  const chooseKind = (next: LedSelectorKind): void => {
    setKind(next);
    const spec = LED_SELECTOR_SPEC[next];
    setSelector(
      next === LED_SELECTOR_KIND.RELAY
        ? [spec.firstValue, spec.firstValue]
        : [moduleId, firstRail?.address ?? BYTE_MIN, BYTE_MIN, BYTE_MIN],
    );
  };

  return (
    <PanelSection
      title="LED acknowledgements"
      description="Which keypad LED lights when an output is switched. Relay and DIN selectors can be read back; DALI LED mappings cannot."
    >
      <div className="space-y-4">
        <SegmentedChoice
          label="Selector"
          value={kind}
          options={[
            {
              value: LED_SELECTOR_KIND.RELAY,
              label: LED_SELECTOR_SPEC[LED_SELECTOR_KIND.RELAY].label,
              description: 'x=[relay, state]',
            },
            {
              value: LED_SELECTOR_KIND.DIN,
              label: LED_SELECTOR_SPEC[LED_SELECTOR_KIND.DIN].label,
              description: 'x=[module, addr, channel, value]',
            },
          ]}
          onChange={chooseKind}
        />

        <PartRow labels={LED_SELECTOR_SPEC[kind].parts} values={selector} onChange={setSelector} />

        <div>
          <p className="text-sm font-medium text-[#333333]">LED · group, addr, led, state</p>
          <div className="mt-2">
            <PartRow
              labels={MAPPING_ACTION_PART_LABEL[MAPPING_ACTION.LED]}
              values={led}
              onChange={setLed}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={inFlight > 0 || !ledField.ok || selector.length !== LED_SELECTOR_SPEC[kind].length}
            onClick={() => {
              if (!ledField.ok) return;
              void send(
                addLedMappings([{ atype: MAPPING_ATYPE.LED_ACK, x: selector, l: ledField.value }]),
                'LED mapping written',
              );
            }}
            className="flex items-center gap-2 rounded-full bg-[#1A1A1A] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#333333] disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            Write LED mapping
          </button>
          <button
            type="button"
            disabled={inFlight > 0}
            onClick={() => void send(readLedMappings([selector]), 'LED mapping read')}
            className="flex items-center gap-2 rounded-full border border-[#E0E0E0] px-4 py-2 text-sm font-medium text-[#666666] transition-colors hover:border-[#D4A054] hover:text-[#333333] disabled:opacity-50"
          >
            <Search className="h-4 w-4" />
            Read
          </button>
          <button
            type="button"
            disabled
            title="The firmware's erase key is longer than the key it saves with, so eraserculedmapping always fails. Overwrite the mapping instead."
            className="flex cursor-not-allowed items-center gap-2 rounded-full border border-[#E0E0E0] px-4 py-2 text-sm font-medium text-[#999999] opacity-60"
          >
            <Trash2 className="h-4 w-4" />
            Erase (not supported)
          </button>
        </div>

        {live.ledMappings.length > 0 && (
          <ul className="space-y-1">
            {live.ledMappings.map((mapping, index) => (
              <li
                key={index}
                className="rounded-xl bg-[#F5F5F5] px-3 py-2 font-mono text-xs text-[#666666]"
              >
                {mapping.atype} · x=[{mapping.x.join(',')}]
                {mapping.actions.map((part) => ` · ${part.field}="${part.raw}"`).join('')}
              </li>
            ))}
          </ul>
        )}

        <div className="rounded-2xl border border-dashed border-[#E0E0E0] px-3 py-3">
          <p className="text-sm font-medium text-[#333333]">DALI LED mapping</p>
          <p className="mt-0.5 text-xs text-[#666666]">
            x=[master, daliaddr, mode, value]. Writable, and there is no endpoint that reads it back,
            so the console cannot show what is stored.
          </p>
          <div className="mt-3">
            <PartRow
              labels={MAPPING_ACTION_PART_LABEL[MAPPING_ACTION.DALI]}
              values={daliLed}
              onChange={setDaliLed}
              wideIndex={modeIndex}
              wideMax={DALI_MODE.TEMP}
            />
          </div>
          <button
            type="button"
            disabled={inFlight > 0 || !ledField.ok}
            onClick={() => {
              if (!ledField.ok) return;
              void send(
                addDaliLedMapping({ atype: MAPPING_ATYPE.DALI_LED_ACK, x: daliLed, l: ledField.value }),
                'DALI LED mapping written',
              );
            }}
            className="mt-3 flex items-center gap-2 rounded-full border border-[#E0E0E0] px-4 py-2 text-sm font-medium text-[#666666] transition-colors hover:border-[#D4A054] hover:text-[#333333] disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            Write DALI LED
          </button>
          <p className="mt-2 text-xs text-[#666666]">
            {daliModeLabel(DALI_MODE.DIM)} is the mode this console offers here; query and temperature
            mappings come from the device’s own tooling.
          </p>
        </div>
      </div>
    </PanelSection>
  );
};

/** Which destructive command the dialog is confirming. */
const DANGER = {
  MAP_ERASE: MAPPING_ATYPE.MAP_ERASE,
  RESTART: MAPPING_ATYPE.RESTART,
  WIFI: MAPPING_ATYPE.WIFI_CONFIG,
} as const;

type DangerCommand = (typeof DANGER)[keyof typeof DANGER];

const DangerZone: React.FC = () => {
  const config = useRcuConfigStore((state) => state.config);
  const { send, inFlight } = useRcuLink();
  const [confirming, setConfirming] = React.useState<DangerCommand | null>(null);
  const [ssid, setSsid] = React.useState('');
  const [password, setPassword] = React.useState('');

  const serial = config.rcu.serialNumber.trim();

  const run = (): void => {
    const target = confirming;
    setConfirming(null);
    switch (target) {
      case DANGER.MAP_ERASE:
        void send(eraseMappingsAndRestart(), 'Erase every mapping');
        return;
      case DANGER.RESTART:
        void send(restart(), 'Restart');
        return;
      case DANGER.WIFI:
        void send(wifiConfig(ssid, password), 'Wi-Fi configuration');
        return;
      default:
        return;
    }
  };

  return (
    <PanelSection
      title="Danger zone"
      description="Commands that erase or reboot. Each one asks for the serial number first."
    >
      <div className="space-y-3">
        <div className="rounded-2xl border border-[#E0E0E0] px-3 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-[#333333]">Legacy Wi-Fi configuration</p>
              <p className="text-xs text-[#666666]">
                Writes credentials into the device’s mapping store and restarts it.
              </p>
            </div>
            <button
              type="button"
              disabled={inFlight > 0}
              onClick={() => setConfirming(DANGER.WIFI)}
              className="flex shrink-0 items-center gap-2 rounded-full border border-[#E0E0E0] px-4 py-2 text-sm font-medium text-[#666666] transition-colors hover:border-[#F44336] hover:text-[#C62828] disabled:opacity-50"
            >
              <Wifi className="h-4 w-4" />
              Write credentials
            </button>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <Field label="SSID" value={ssid} onChange={setSsid} />
            <Field label="Password" value={password} onChange={setPassword} secret />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#E0E0E0] px-3 py-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-[#333333]">Restart the RCU</p>
            <p className="text-xs text-[#666666]">
              Reboots two seconds after the command is accepted. Any scene running stops.
            </p>
          </div>
          <button
            type="button"
            disabled={inFlight > 0}
            onClick={() => setConfirming(DANGER.RESTART)}
            className="shrink-0 rounded-full border border-[#E0E0E0] px-4 py-2 text-sm font-medium text-[#666666] transition-colors hover:border-[#F44336] hover:text-[#C62828] disabled:opacity-50"
          >
            Restart
          </button>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#F44336]/40 bg-[#F44336]/5 px-3 py-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-[#C62828]">Erase every mapping</p>
            <p className="text-xs text-[#666666]">
              Empties the mapping namespace — scenes, LED maps and any legacy Wi-Fi credentials in it
              — then restarts. There is no undo.
            </p>
          </div>
          <button
            type="button"
            disabled={inFlight > 0}
            onClick={() => setConfirming(DANGER.MAP_ERASE)}
            className="shrink-0 rounded-full bg-[#F44336] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#D32F2F] disabled:opacity-50"
          >
            Erase all
          </button>
        </div>
      </div>

      <TypedConfirmDialog
        isOpen={confirming !== null}
        title={
          confirming === DANGER.MAP_ERASE
            ? 'Erase every mapping'
            : confirming === DANGER.RESTART
              ? 'Restart the RCU'
              : 'Write Wi-Fi credentials'
        }
        message={
          confirming === DANGER.MAP_ERASE
            ? 'One mapping command with atype maperase empties the mapping namespace and restarts the device.'
            : confirming === DANGER.RESTART
              ? 'The device reboots two seconds after the command is accepted.'
              : 'The credentials are stored in the mapping namespace and the device restarts. The password is redacted from the event feed.'
        }
        expected={serial === '' ? 'the RCU serial number' : serial}
        confirmLabel="Send"
        onCancel={() => setConfirming(null)}
        onConfirm={run}
      />
    </PanelSection>
  );
};

/**
 * Scene, LED and destructive mapping commands in one place. The scene editor
 * builds the action strings the firmware parses positionally, so the preview
 * shows exactly what will be sent.
 */
export const MappingsPanel: React.FC = () => (
  <div className="space-y-5">
    <SceneEditor />
    <LedMappings />
    <DangerZone />
    <PanelSection
      title="How mappings are read"
      description="The firmware has no read-all endpoint, so a mapping is read one trigger at a time."
    >
      <ul className="space-y-1.5 text-sm text-[#666666]">
        <li className="flex gap-2">
          <ListTree className="mt-0.5 h-4 w-4 shrink-0 text-[#D4A054]" />
          <span>
            A read is loaded into a 512-byte buffer on the device and published in a 511-byte slot, so
            a large scene set can come back truncated. Read the scenes you changed.
          </span>
        </li>
        <li className="flex gap-2">
          <ListTree className="mt-0.5 h-4 w-4 shrink-0 text-[#D4A054]" />
          <span>
            An entry that does not exist is simply absent from the reply, so “nothing read” and “no
            scene here” look the same.
          </span>
        </li>
      </ul>
    </PanelSection>
  </div>
);

export default MappingsPanel;
